// components/events/EventDetails.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { db } from "../../firebase";
import { doc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import { Container, Row, Col, Card, Button, Nav, Spinner, Alert } from "react-bootstrap";
import { useAuth } from "../../rbac/AuthContext";
import useCommunityResidents from "../../hooks/useCommunityResidents";
import OverviewPanel from "./OverviewPanel";
import FundsPanel from "./FundsPanel";
import ExpensesPanel from "./ExpensesPanel";
import CommitteePanel from "./CommitteePanel";
import { ROLE_PRESETS, cleanObject, normalizeCommitteeItem } from "../../utils/eventUtils";

export default function EventDetails() {
  const { communityId, eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // const auth = getAuth();
  // const currentUid = auth.currentUser?.uid || null;

  // const { isAdmin, isManager, role } = useAuth();
  // const canEditFunds = isAdmin || isManager;
  // const canEditExpenses = isAdmin || isManager;
  // const canEditCommittee = isAdmin || isManager;

  const auth = getAuth();
const currentUid = auth.currentUser?.uid || null;


const { isAdmin, isManager, role } = useAuth();
const [tab, setTab] = useState("overview");


  const { community, residents, loading: commLoading, error: commError } =
    useCommunityResidents(communityId);

  // Event doc
  const [eventDoc, setEventDoc] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [eventError, setEventError] = useState("");

  /*
 * Check whether current user
 * is a committee member of this event
 */

  const currentResident = useMemo(() => {
  if (!currentUid) return null;

  return residents.find(
    (resident) => resident.uid === currentUid
  )
}
)

  const isCommitteeMember = useMemo(() => {
  if (!currentUid) return false;

  const committee = eventDoc?.committee || [];

  return committee.some(
    (member) => member?.uid === currentUid
  );
}, [currentUid, eventDoc]);

const canEditFunds =
  isAdmin ||
  isManager ||
  isCommitteeMember;

const canEditExpenses =
  isAdmin ||
  isManager ||
  isCommitteeMember;

const canEditCommittee =
  isAdmin ||
  isManager;

  useEffect(() => {
    if (!eventId) {
      setEventError("Missing eventId in URL.");
      setEventLoading(false);
      return;
    }
    setEventLoading(true);
    setEventError("");
    const ref = doc(db, "events", eventId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setEventError("Event not found.");
          setEventDoc(null);
        } else {
          setEventDoc({ id: snap.id, ...snap.data() });
        }
        setEventLoading(false);
      },
      (err) => {
        console.error("Event snapshot error:", err);
        setEventError(err?.message || "Failed to load event.");
        setEventLoading(false);
      }
    );
    return () => unsub();
  }, [eventId]);

  const handleBack = useCallback(() => {
    if (location.key !== "default") {
      navigate(-1);
      return;
    }
    const r = (role || "").toLowerCase();
    if (r === "admin") {
      navigate(`/communities/${communityId}`);
      return;
    }
    if (r === "manager") {
      navigate(`/manager/${communityId}`);
      return;
    }
    navigate(`/resident/${communityId}`);
  }, [navigate, location.key, role, communityId]);

  const coreLoading = eventLoading || commLoading;

  const MobileTabs = (
    <div className="sticky-mobile-tabs bg-white border-bottom d-md-none">
      <div className="px-2 py-2 mobile-nav-scroll">
        <Nav variant="underline" activeKey={tab} onSelect={(k) => k && setTab(k)} className="flex-nowrap">
          <Nav.Item><Nav.Link eventKey="overview" className="text-capitalize">Overview</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="funds" className="text-capitalize">Funds</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="expenses" className="text-capitalize">Expenses</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="committee" className="text-capitalize">Committee</Nav.Link></Nav.Item>
        </Nav>
      </div>
      <div className="px-2 pb-2">
        <Button variant="outline-secondary" size="sm" className="w-100" onClick={handleBack}>
          ← Back
        </Button>
      </div>
    </div>
  );

  return (
    <Container fluid className="mt-3">
      <style>{`
        .mobile-nav-scroll { overflow-x: auto; white-space: nowrap; -webkit-overflow-scrolling: touch; }
        .sticky-mobile-tabs { position: sticky; top: 0; z-index: 1020; }
        .mobile-nav-scroll .nav-link { padding: .5rem .75rem; font-weight: 600; }
      `}</style>

      {MobileTabs}

      <Row className="mt-2 mt-md-0">
        {/* Left nav */}
        <Col md={3} lg={2} className="d-none d-md-block">
          <Card>
            <Card.Header>
              <strong>{eventDoc?.name || "Event"}</strong>
            </Card.Header>
            <Nav className="flex-column p-2">
              <Nav.Link active={tab === "overview"} onClick={() => setTab("overview")}>Overview</Nav.Link>
              <Nav.Link active={tab === "funds"} onClick={() => setTab("funds")}>Funds</Nav.Link>
              <Nav.Link active={tab === "expenses"} onClick={() => setTab("expenses")}>Expenses</Nav.Link>
              <Nav.Link active={tab === "committee"} onClick={() => setTab("committee")}>Committee</Nav.Link>
            </Nav>
            <div className="border-top" />
            <div className="p-2">
              <Button variant="outline-secondary" size="sm" className="w-100" onClick={handleBack}>
                ← Back
              </Button>
            </div>
          </Card>
        </Col>

        {/* Right content */}
        <Col md={9} lg={10}>
          {coreLoading ? (
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Loading event…</span>
            </div>
          ) : eventError ? (
            <Alert variant="danger">{eventError}</Alert>
          ) : commError ? (
            <Alert variant="danger">{commError}</Alert>
          ) : (
            <>
              {tab === "overview" && (
                <OverviewPanel
                  event={eventDoc}
                  community={community}
                  residents={residents}
                  currentResident={currentResident}
                  canEdit={isAdmin || isManager}
                />
              )}
              {tab === "funds" && (
                <FundsPanel
                  communityId={communityId}
                  event={eventDoc}
                   community={community}
                  residents={residents}
                  canEditFunds={canEditFunds}
                />
              )}
              {tab === "expenses" && (
                <ExpensesPanel
                  communityId={communityId}
                  event={eventDoc}
                  residents={residents}
                  canEditExpenses={canEditExpenses}
                />
              )}
              {tab === "committee" && (
                <CommitteePanel
                  event={eventDoc}
                   community={community}
                  residents={residents}
                  canEditCommittee={canEditCommittee}
                />
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
}

